import React, { useEffect, useMemo, useRef, useState } from "react"
import { Plus } from "lucide-react"
import Badge from "../common/Badge.jsx"
import Button from "../common/Button.jsx"
import TableWithPagination from "../table/TableWithPagination.jsx"
import { formatContactMethodLabel } from "../utils/contactMethodLabel.js"

const HEADER_CLASS = "text-uppercase text-secondary"
const HEADER_STYLE = { fontSize: "0.75rem", fontWeight: 500 }

// Debug-only: set true to render the hidden measurement rows.
const DEBUG_SHOW_STAFF_BADGE_MEASURES = false

const TRUNCATE_LINE_STYLE = {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
}

/** Fixed row height for Fit measurement + uniform layout (see TableWithPagination). */
const CUSTOMER_TABLE_ROW_HEIGHT_PX = 82

/** Matches Bootstrap `.table` tbody cell padding (same as Status column). */
const TABLE_TD_PADDING = "0.5rem"
const TABLE_TD_PADDING_TOP = "0.75rem"
const TABLE_TD_PADDING_BOTTOM = TABLE_TD_PADDING

const CUSTOMER_ROW_TD_STYLE = {
    verticalAlign: "top",
    overflow: "hidden",
    paddingTop: TABLE_TD_PADDING_TOP,
}

const SELECT_CHECKBOX_TD_STYLE = {
    verticalAlign: "middle",
    paddingTop: TABLE_TD_PADDING,
    paddingBottom: TABLE_TD_PADDING,
    paddingLeft: TABLE_TD_PADDING,
    paddingRight: TABLE_TD_PADDING,
}

/** Type column: wrapping badges need normal whitespace + cell height for Fit row. */
const TYPE_BADGE_CELL_STYLE = {
    minWidth: "180px",
    verticalAlign: "top",
    whiteSpace: "normal",
    width: "auto",
    boxSizing: "border-box",
    paddingTop: TABLE_TD_PADDING_TOP,
    paddingBottom: TABLE_TD_PADDING_BOTTOM,
    paddingLeft: TABLE_TD_PADDING,
    paddingRight: TABLE_TD_PADDING,
    overflow: "visible",
    maxHeight: `${CUSTOMER_TABLE_ROW_HEIGHT_PX}px`,
}

/**
 * @param {string} key
 * @param {any} label
 * @param {number} minWidth
 * @param {number | undefined} [preferredWidth]
 */
function makeHeader(key, label, minWidth, preferredWidth = undefined) {
    return {
        key,
        className: HEADER_CLASS,
        style: HEADER_STYLE,
        label,
        minWidth,
        preferredWidth,
    }
}

function renderTextCell(value, className = "", style = {}) {
    const displayValue = value ?? ""
    return (
        <td style={style} className={className}>
            <span
                title={displayValue}
                style={{
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >
                {displayValue}
            </span>
        </td>
    )
}

function renderComponentCell(content, style = {}) {
    return (
        <td
            style={{
                // Callers may override (e.g. Type column: wrap + width auto).
                whiteSpace: "nowrap",
                width: "1%",
                ...style,
            }}
        >
            {content}
        </td>
    )
}

/**
 * Customer name + Staff badge that compacts the badge before truncating the name.
 * If "Full name + full Staff badge" won't fit, the badge becomes compact ("S", less padding).
 * If it still won't fit, the name truncates (ellipsis).
 */
function CustomerNameRow({ fullName, isStaff }) {
    const containerRef = useRef(/** @type {HTMLDivElement | null} */(null))
    const measureFullRef = useRef(/** @type {HTMLDivElement | null} */(null))
    const [useCompactBadge, setUseCompactBadge] = useState(false)

    useEffect(() => {
        if (!isStaff) {
            setUseCompactBadge(false)
            return
        }

        function recompute() {
            const container = containerRef.current
            const full = measureFullRef.current
            if (!container || !full) return

            const available = container.clientWidth
            const fullWidth = full.scrollWidth

            // If the expanded version doesn't fit, use compact (and let the name truncate if needed).
            const nextCompact = available > 0 ? fullWidth > available : true
            setUseCompactBadge((prev) => (prev === nextCompact ? prev : nextCompact))
        }

        recompute()

        const ro = new ResizeObserver(recompute)
        if (containerRef.current) {
            ro.observe(containerRef.current)
        }

        window.addEventListener("resize", recompute)
        return () => {
            window.removeEventListener("resize", recompute)
            ro.disconnect()
        }
    }, [isStaff, fullName])

    return (
        <div
            ref={containerRef}
            className="d-flex align-items-start gap-2 flex-grow-1"
            style={{ minWidth: 0, flex: "1 1 0", position: "relative" }}
        >
            {/* Hidden duplicates for deterministic width measurement (no layout impact). */}
            {isStaff ? (
                <div
                    style={{
                        position: "absolute",
                        left: 0,
                        top: DEBUG_SHOW_STAFF_BADGE_MEASURES ? "100%" : 0,
                        visibility: DEBUG_SHOW_STAFF_BADGE_MEASURES ? "visible" : "hidden",
                        pointerEvents: "none",
                        whiteSpace: "nowrap",
                        zIndex: DEBUG_SHOW_STAFF_BADGE_MEASURES ? 10 : undefined,
                        backgroundColor: DEBUG_SHOW_STAFF_BADGE_MEASURES ? "rgba(255, 193, 7, 0.20)" : undefined,
                    }}
                    aria-hidden="true"
                >
                    <div
                        ref={measureFullRef}
                        className="d-flex align-items-start gap-2"
                        style={{ width: "max-content", whiteSpace: "nowrap" }}
                    >
                        <div className="fw-semibold text-dark" style={{ fontSize: "0.875rem", whiteSpace: "nowrap" }}>
                            {fullName}
                        </div>
                        <Badge variant="info" className="flex-shrink-0">
                            Staff
                        </Badge>
                    </div>
                </div>
            ) : null}

            <div
                className="fw-semibold text-dark"
                style={{
                    fontSize: "0.875rem",
                    flex: "0 1 auto",
                    maxWidth: "100%",
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
                title={fullName}
            >
                {fullName}
            </div>

            {isStaff ? (
                <Badge
                    variant="info"
                    className="flex-shrink-0"
                    title="Staff"
                    aria-label="Staff"
                    paddingX={useCompactBadge ? "0.5rem" : undefined}
                >
                    {useCompactBadge ? "S" : "Staff"}
                </Badge>
            ) : null
            }
        </div >
    )
}

function renderCustomerCell(customer) {
    const initials = `${customer.first_name?.[0] ?? ""}${customer.last_name?.[0] ?? ""
        }`.toUpperCase()
    const fullName = `${customer.first_name ?? "—"} ${customer.last_name ?? "—"}`

    return (
        <td style={{ minWidth: "220px", ...CUSTOMER_ROW_TD_STYLE }}>
            <div className="d-flex align-items-start">
                <div className="flex-shrink-0">
                    <div
                        className="rounded-circle d-flex align-items-center
                            justify-content-center"
                        style={{
                            width: "40px",
                            height: "40px",
                            backgroundColor: "#0f6170",
                        }}
                    >
                        <span className="text-white fw-semibold"
                            style={{ fontSize: "0.875rem" }}>
                            {initials || "?"}
                        </span>
                    </div>
                </div>
                <div className="ms-3 flex-grow-1" style={{ minWidth: 0 }}>
                    <CustomerNameRow fullName={fullName} isStaff={Boolean(customer.is_staff)} />
                    <div className="text-secondary" style={{ fontSize: "0.75rem", minWidth: 0 }}>
                        <span
                            style={TRUNCATE_LINE_STYLE}
                            title={`ID: ${customer?.id ?? "—"}`}
                        >
                            ID: {customer?.id ?? "—"}
                        </span>
                    </div>
                    {customer.company && (
                        <div className="text-secondary" style={{ fontSize: "0.75rem", minWidth: 0 }}>
                            <span style={TRUNCATE_LINE_STYLE} title={String(customer.company ?? "")}>
                                {customer.company}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </td>
    )
}

function getStatusBadgeVariant(status) {
    return status === "active" ? "success" : "error"
}

/**
 * @param {{
 *   customers: any[],
 *   isLoadingInitial?: boolean,
 *   totalRows?: number,
 *   page?: number,
 *   perPage?: number,
 *   onPageChange?: (nextPage: number) => void,
 *   onPerPageChange?: (nextPerPage: number | "fit") => void,
 *   perPageSelection?: number | "fit",
 *   onFitPerPageChange?: (nextFitPerPage: number) => void,
 *   onCustomerClick?: (customer: any) => void,
 *   onAddOrderClick?: (customer: any) => void,
 *   showRefetchLoader?: boolean,
 *   enableStatusColumn?: boolean,
 *   enableTypeColumn?: boolean,
 *   selectedCustomerIds?: Array<string | number>,
 *   onSelectedCustomerIdsChange?: (next: Array<string | number>) => void,
 * }} props
 */
export default function CustomersDesktopTable({
    customers,
    isLoadingInitial = false,
    totalRows = 0,
    page = 1,
    perPage = 25,
    onPageChange,
    onPerPageChange,
    perPageSelection = undefined,
    onFitPerPageChange = undefined,
    onCustomerClick = undefined,
    onAddOrderClick = undefined,
    showRefetchLoader = false,
    enableStatusColumn = true,
    enableTypeColumn = true,
    selectedCustomerIds = undefined,
    onSelectedCustomerIdsChange = undefined,
}) {
    const [internalSelectedIds, setInternalSelectedIds] = useState(
        () => /** @type {Array<string|number>} */([])
    )

    const customerIdsOnPage = useMemo(
        () => (customers ?? []).map((c) => c?.id).filter((id) => id != null),
        [customers]
    )

    const selectedIds = Array.isArray(selectedCustomerIds) ? selectedCustomerIds : internalSelectedIds
    const setSelectedIds = typeof onSelectedCustomerIdsChange === "function" ? onSelectedCustomerIdsChange : setInternalSelectedIds

    useEffect(() => {
        // Clear multi-select when the visible page changes to avoid acting on off-screen results.
        setSelectedIds([])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, perPage, customerIdsOnPage.join("|")])

    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])
    const selectedCount = selectedIds.length

    const allSelectedOnPage = (
        customerIdsOnPage.length > 0 &&
        customerIdsOnPage.every((id) => selectedSet.has(id))
    )
    const someSelectedOnPage = (
        customerIdsOnPage.some((id) => selectedSet.has(id)) && !allSelectedOnPage
    )

    const SELECT_CHECKBOX_STYLE = {
        width: "1rem",
        height: "1rem",
        marginTop: 0,
    }

    const headerCheckboxRef = React.useRef(/** @type {HTMLInputElement | null} */(null))
    useEffect(() => {
        if (headerCheckboxRef.current) {
            headerCheckboxRef.current.indeterminate = someSelectedOnPage
        }
    }, [someSelectedOnPage])

    function toggleSelectAllOnPage() {
        setSelectedIds((prev) => {
            const prevSet = new Set(prev)
            const nextSet = new Set(prev)
            if (customerIdsOnPage.length === 0) {
                return prev
            }
            const shouldSelectAll = !customerIdsOnPage.every((id) => prevSet.has(id))
            if (shouldSelectAll) {
                customerIdsOnPage.forEach((id) => nextSet.add(id))
            } else {
                customerIdsOnPage.forEach((id) => nextSet.delete(id))
            }
            return Array.from(nextSet)
        })
    }

    function toggleSelectOne(customerId) {
        setSelectedIds((prev) => {
            const next = new Set(prev)
            if (next.has(customerId)) {
                next.delete(customerId)
            } else {
                next.add(customerId)
            }
            return Array.from(next)
        })
    }

    function formatLatestOrderDateLabel(value) {
        if (!value) {
            return "None"
        }
        const parsed = new Date(value)
        if (!Number.isFinite(parsed.getTime())) {
            return String(value)
        }
        return parsed.toISOString().slice(0, 10)
    }

    function formatShipToLocation(customer) {
        const city = customer?.ship_to?.city ?? ""
        const state = customer?.ship_to?.state ?? ""
        const trimmedCity = String(city).trim()
        const trimmedState = String(state).trim()

        if (!trimmedCity && !trimmedState) {
            return "—"
        }
        if (!trimmedCity) {
            return trimmedState
        }
        if (!trimmedState) {
            return trimmedCity
        }
        return `${trimmedCity}, ${trimmedState}`
    }

    const headers = [
        {
            ...makeHeader(
                "select",
                <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    className="form-check-input align-middle"
                    style={SELECT_CHECKBOX_STYLE}
                    checked={allSelectedOnPage}
                    onChange={() => toggleSelectAllOnPage()}
                    onClick={(e) => e.stopPropagation()}
                    aria-label="Select all customers on this page"
                />,
                60,
                60
            ),
            minWidth: 35,
            preferredWidth: 35,
        },
        makeHeader("customer", "Customer", 120, 300),
        makeHeader("contact", "Contact", 100, 260),
        ...(enableStatusColumn ? [makeHeader("status", <span title="Showing mock data">Status</span>, 60, 90)] : []),
        ...(enableTypeColumn ? [makeHeader("type", <span title="Showing mock data">Type</span>, 60, 180)] : []),
        makeHeader("total_orders", "Total Orders", 60, 100),
        makeHeader("latest_order_date", "Last Order", 60, 120),
        makeHeader("location", "Location", 80, 180),
        makeHeader("actions", "Actions", 80, 110),
    ]

    function renderCustomerRow(customer) {
        const customerId = customer?.id
        const isChecked = customerId != null && selectedSet.has(customerId)
        const rowDims = {
            height: CUSTOMER_TABLE_ROW_HEIGHT_PX,
            minHeight: CUSTOMER_TABLE_ROW_HEIGHT_PX,
            maxHeight: CUSTOMER_TABLE_ROW_HEIGHT_PX,
        }
        return (
            <tr
                key={customer.id}
                style={{ cursor: "pointer", boxSizing: "border-box", ...rowDims }}
                onClick={() => {
                    if (onCustomerClick) {
                        onCustomerClick(customer)
                    }
                }}
            >
                <td style={SELECT_CHECKBOX_TD_STYLE}>
                    <div
                        onClick={(event) => event.stopPropagation()}
                        className="d-flex align-items-center"
                    >
                        <input
                            type="checkbox"
                            className="form-check-input align-middle"
                            style={SELECT_CHECKBOX_STYLE}
                            checked={Boolean(isChecked)}
                            onChange={() => {
                                if (customerId == null) {
                                    return
                                }
                                toggleSelectOne(customerId)
                            }}
                            onClick={(event) => event.stopPropagation()}
                            aria-label={`Select customer ${customer?.id ?? ""}`}
                        />
                    </div>
                </td>
                {renderCustomerCell(customer)}
                <td style={{ minWidth: "220px", ...CUSTOMER_ROW_TD_STYLE }}>
                    <div className="text-dark" style={{ fontSize: "0.875rem", minWidth: 0 }}>
                        <span style={TRUNCATE_LINE_STYLE} title={customer.email ?? "—"}>
                            {customer.email ?? "—"}
                        </span>
                    </div>
                    <div className="text-secondary" style={{ fontSize: "0.875rem", minWidth: 0 }}>
                        <span style={TRUNCATE_LINE_STYLE} title={customer.phone ?? "—"}>
                            {customer.phone ?? "—"}
                        </span>
                    </div>
                    <div className="text-muted text-capitalize" style={{ fontSize: "0.75rem", minWidth: 0 }}>
                        <span
                            style={TRUNCATE_LINE_STYLE}
                            title={`Prefers ${formatContactMethodLabel(customer.contact_method) || "—"}`}
                        >
                            Prefers {formatContactMethodLabel(customer.contact_method) || "—"}
                        </span>
                    </div>
                </td>
                {enableStatusColumn ? renderComponentCell(
                    <Badge
                        variant={getStatusBadgeVariant(customer.status)}
                        className="v2-not-implemented-btn"
                        title="Showing mock data"
                    >
                        {customer.status}
                    </Badge>,
                    { ...CUSTOMER_ROW_TD_STYLE }
                ) : null}
                {enableTypeColumn ? renderComponentCell(
                    <div
                        className="d-flex flex-wrap"
                        style={{
                            minWidth: 0,
                            width: "100%",
                            boxSizing: "border-box",
                            gap: "4px",
                            alignContent: "flex-start",
                            alignItems: "flex-start",
                            maxHeight:
                                `calc(${CUSTOMER_TABLE_ROW_HEIGHT_PX}px - ${TABLE_TD_PADDING_TOP} - ${TABLE_TD_PADDING_BOTTOM})`,
                            overflow: "visible",
                        }}
                    >
                        {(customer.customer_types ?? []).map((type) => (
                            <Badge
                                key={type}
                                variant="info"
                                className="small v2-not-implemented-btn"
                                title="Showing mock data"
                            >
                                {type}
                            </Badge>
                        ))}
                    </div>,
                    { ...TYPE_BADGE_CELL_STYLE }
                ) : null}
                {renderTextCell(
                    String(customer.total_orders),
                    "text-dark",
                    { fontSize: "0.875rem", ...CUSTOMER_ROW_TD_STYLE }
                )}
                {renderTextCell(
                    formatLatestOrderDateLabel(customer.latest_order_date),
                    "text-dark",
                    { fontSize: "0.875rem", ...CUSTOMER_ROW_TD_STYLE }
                )}
                {renderTextCell(
                    formatShipToLocation(customer),
                    "text-dark",
                    { fontSize: "0.875rem", ...CUSTOMER_ROW_TD_STYLE }
                )}
                {renderComponentCell(
                    <div onClick={(event) => event.stopPropagation()}>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onAddOrderClick?.(customer)}
                        >
                            <span className="me-1"><Plus size={14} /></span>
                            Order
                        </Button>
                    </div>,
                    { ...CUSTOMER_ROW_TD_STYLE }
                )}
            </tr>
        )
    }

    return (
        <TableWithPagination
            headers={headers}
            rows={customers}
            renderRow={renderCustomerRow}
            isLoadingInitial={isLoadingInitial}
            totalRows={totalRows}
            page={page}
            perPage={perPage}
            onPageChange={onPageChange}
            onPerPageChange={onPerPageChange}
            perPageSelection={perPageSelection}
            onFitPerPageChange={onFitPerPageChange}
            stretchFooterToBottom={true}
            showRefetchLoader={showRefetchLoader}
        />
    )
}
